/**
 * Status utilities for consistent status display across the application
 */

export type DiscussionStatus =
  | "None"
  | "Open"
  | "Processed"
  | "Rework Required"
  | "Resolved"
  | "Closed"
  | "processed"
  | "rework_required"
  | string;

/**
 * Normalize status values to display names
 */
export const normalizeStatus = (status?: string): string => {
  if (!status) return "Open";

  const normalized = status.toLowerCase().trim();

  // Map internal values to display values
  const statusMap: Record<string, string> = {
    none: "Open",
    open: "Open",
    processed: "Processed",
    rework_required: "Rework Required",
    resolved: "Resolved",
    closed: "Closed",
  };

  return statusMap[normalized] || status;
};

export type DiscussionStatusGroup = "open" | "in_progress" | "closed";

/**
 * The group a status counts under in the Open / In Progress / Resolved statistics and the status
 * filter. The Discussions page and the dashboard both use this, so their counts always agree.
 * Returns null for a status outside the three groups, which is then not counted.
 */
export const getStatusGroup = (status?: string): DiscussionStatusGroup | null => {
  const normalized = normalizeStatus(status).toLowerCase();

  if (normalized === "open") return "open";
  if (normalized === "processed" || normalized === "rework required") return "in_progress";
  if (normalized === "resolved" || normalized === "closed") return "closed";

  return null;
};

/**
 * Get the primary color for a status
 */
export const getStatusColor = (status?: string): string => {
  const normalized = normalizeStatus(status).toLowerCase();

  if (normalized.includes("open") || normalized.includes("none")) {
    return "#D73B02"; // Orange-Red
  } else if (normalized.includes("processed")) {
    return "#0078D4"; // Blue
  } else if (normalized.includes("rework")) {
    return "#FF8C00"; // Orange
  } else if (normalized.includes("resolved") || normalized.includes("closed")) {
    return "#107C10"; // Green
  }

  return "#8A8886"; // Default gray
};

/**
 * Get a lighter version of the status color (for backgrounds)
 */
export const getStatusColorLight = (status?: string): string => {
  const normalized = normalizeStatus(status).toLowerCase();

  if (normalized.includes("open") || normalized.includes("none")) {
    return "#FDE7E9"; // Light Orange-Red
  } else if (normalized.includes("processed")) {
    return "#D6EBFF"; // Light Blue
  } else if (normalized.includes("rework")) {
    return "#FFE5CC"; // Light Orange
  } else if (normalized.includes("resolved") || normalized.includes("closed")) {
    return "#DFF6DD"; // Light Green
  }

  return "#F3F2F1"; // Default light gray
};

/**
 * Get a darker version of the status color (for text/borders)
 */
export const getStatusColorDark = (status?: string): string => {
  const normalized = normalizeStatus(status).toLowerCase();

  if (normalized.includes("open") || normalized.includes("none")) {
    return "#A72E00"; // Dark Orange-Red
  } else if (normalized.includes("processed")) {
    return "#005A9E"; // Dark Blue
  } else if (normalized.includes("rework")) {
    return "#CC7000"; // Dark Orange
  } else if (normalized.includes("resolved") || normalized.includes("closed")) {
    return "#0B5A08"; // Dark Green
  }

  return "#605E5C"; // Default dark gray
};

/**
 * Get the background color with opacity for status badges
 */
export const getStatusColorBackground = (status?: string, opacity: number = 0.1): string => {
  const color = getStatusColor(status);
  // Convert hex to rgba
  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/**
 * Get status icon color. Slightly different from getStatusColor so the icon
 * tone matches the DiscussionStatistics panel (grey for "open" instead of the
 * red used on badges). Other statuses defer to getStatusColor.
 */
export const getStatusIconColor = (status?: string): string => {
  const normalized = normalizeStatus(status).toLowerCase();
  if (normalized.includes("open") || normalized.includes("none")) {
    return "#5b6e7f"; // Match the grey used in DiscussionStatistics
  }
  return getStatusColor(status);
};
